import { useCallback, useEffect, useState } from "react";
import { useSpark } from "@/lib/store";
import { getProfile, getWeave, proposeWeave, respondWeave, weaveAgain } from "@/lib/api";
import { toast } from "sonner";
import { GoldThreadLine } from "@/components/SparkSun";

const GOLD = "#C3A56B";
const GOLD_DEEP = "#A6894F";
const MAP_ORDER = ["strengths", "energy", "values"];

const MapColumn = ({ name, map, used }) => (
  <div data-testid={`weave-map-${name}`} className="min-w-0">
    <p className="spark-eyebrow">{map.label}</p>
    <ul className="mt-3 space-y-1.5">
      {map.labels.length === 0 && <li className="text-sm text-[#8A8A91]">Nothing confirmed yet</li>}
      {map.labels.map(l => {
        const on = used.has(l.toLowerCase());
        return (
          <li key={l} className="flex items-start gap-2 text-sm leading-snug" style={{ color: on ? "#17171C" : "#8A8A91" }}>
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: on ? GOLD : "rgba(0,0,0,0.15)" }} />
            <span>{l}</span>
          </li>
        );
      })}
    </ul>
  </div>
);

const Proposal = ({ p, onRespond, busy }) => {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  return (
    <div data-testid="weave-proposal">
      <p className="spark-eyebrow" style={{ color: GOLD_DEEP }}>A Gold Thread may run through all of this</p>
      <h3 data-testid="weave-theme" className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" style={{ color: GOLD_DEEP }}>{p.theme}.</h3>
      <p data-testid="weave-connection" className="mt-5 spark-voice text-lg sm:text-xl leading-relaxed text-[#17171C] max-w-3xl">{p.connection}</p>
      {p.woven_from?.length > 0 && (
        <ul data-testid="weave-woven-from" className="mt-6 grid gap-3 sm:grid-cols-3">
          {p.woven_from.map(x => (
            <li key={x.label} className="rounded-xl border p-4 bg-white" style={{ borderColor: "rgba(195,165,107,0.45)" }}>
              <p className="spark-eyebrow !text-[10px]">{x.map}</p>
              <p className="mt-1 text-sm font-semibold text-[#17171C]">{x.label}</p>
              <p className="mt-1.5 text-sm leading-snug text-[#56565D]">{x.role}</p>
            </li>
          ))}
        </ul>
      )}
      {p.counter && (
        <div data-testid="weave-counter" className="mt-6 rounded-xl p-5" style={{ background: "hsl(var(--spark-cool))" }}>
          <p className="spark-eyebrow">Where it doesn't quite fit</p>
          <p className="mt-2 text-base leading-relaxed text-[#17171C]">{p.counter}</p>
        </div>
      )}
      <p className="mt-6 text-sm text-[#56565D]">{p.check}</p>
      {!mode ? (
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="weave-yes-btn" disabled={busy} onClick={() => onRespond("yes", "")} className="spark-btn-primary disabled:opacity-60">Yes, that runs through it</button>
          <button data-testid="weave-partly-btn" disabled={busy} onClick={() => setMode("partly")} className="spark-btn-secondary disabled:opacity-60">Partly</button>
          <button data-testid="weave-no-btn" disabled={busy} onClick={() => setMode("no")} className="spark-btn-secondary disabled:opacity-60">No, not quite</button>
        </div>
      ) : (
        <div className="mt-4 max-w-2xl">
          <textarea data-testid="weave-correction-input" value={note} onChange={e => setNote(e.target.value)} rows={3}
                    placeholder={mode === "partly" ? "Which part runs through it, and which part doesn't?" : "Tell me what I've missed."}
                    className="w-full rounded-xl border bg-white px-4 py-3 text-base leading-relaxed focus:outline-none focus:ring-2" style={{ borderColor: "hsl(var(--input))", "--tw-ring-color": "rgba(44,80,160,0.35)" }} />
          <div className="mt-3 flex gap-2">
            <button data-testid="weave-submit-correction-btn" disabled={busy} onClick={() => onRespond(mode, note)} className="spark-btn-primary disabled:opacity-60">Save</button>
            <button onClick={() => { setMode(null); setNote(""); }} className="spark-btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Gold Thread Weave — one thread across confirmed Strengths, Energy and Values. Logic lives on the server.
export default function GoldThreadWeave({ compact = false }) {
  const { userId, profile, setProfile } = useSpark();
  const [w, setW] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => { try { setW(await getWeave(userId)); } catch { /* polling tolerance */ } }, [userId]);
  useEffect(() => { refresh(); }, [refresh, profile?.updated_at]);
  useEffect(() => {
    if (!w?.weaving) return;
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [w?.weaving, refresh]);

  if (!w) return null;
  const woven = w.woven || [];
  if (!w.ready && woven.length === 0) {
    if (compact) return null;
    return (
      <section data-testid="weave-not-ready" className="rounded-xl border p-6" style={{ borderColor: "rgba(195,165,107,0.35)" }}>
        <p className="spark-eyebrow" style={{ color: GOLD_DEEP }}>The Gold Thread · Weave</p>
        <p className="mt-2 text-base text-[#56565D] leading-relaxed">Once you've confirmed patterns in at least two of your Strengths, Energy and Values maps, Spark can look across them for the one thread that connects them.</p>
        <p className="mt-2 text-sm text-[#8A8A91]">Confirmed so far: {MAP_ORDER.map(k => `${w.maps[k].label} ${w.maps[k].confirmed}`).join(" · ")}</p>
      </section>
    );
  }

  const act = async (fn, msg) => {
    setBusy(true);
    try { setW(await fn()); if (msg) toast(msg); }
    catch (e) { toast(e?.response?.data?.detail || "Something interrupted Spark. Please try again."); }
    finally { setBusy(false); }
  };
  const respond = async (status, correction) => {
    await act(() => respondWeave(userId, { status, correction }),
      status === "yes" ? "Held as your Gold Thread." : status === "partly" && correction ? "Spark is re-weaving from your correction." : "Set aside. Spark won't push this one again.");
    if (status === "yes") setProfile(await getProfile(userId));
  };
  const used = new Set((w.proposal?.woven_from || []).map(x => x.label.toLowerCase()));

  return (
    <section data-testid="weave-panel" className="relative">
      <div className="flex items-center gap-3 mb-5">
        <GoldThreadLine className="w-16 h-2" />
        <span className="spark-eyebrow" style={{ color: GOLD_DEEP }}>The Gold Thread · Weave</span>
      </div>

      {!compact && (
        <div className="grid gap-6 sm:grid-cols-3 pb-8 mb-8" style={{ borderBottom: "1px solid rgba(195,165,107,0.35)" }}>
          {MAP_ORDER.map(k => <MapColumn key={k} name={k} map={w.maps[k]} used={used} />)}
        </div>
      )}

      {w.weaving && (
        <p data-testid="weave-thinking" className="spark-voice text-lg text-[#56565D]">Spark is reading across your maps for the one thread that runs through them…</p>
      )}
      {!w.weaving && w.error && <p data-testid="weave-error" className="text-sm text-[#1F3D80]">{w.error}</p>}

      {!w.weaving && w.status === "proposed" && w.proposal && <Proposal p={w.proposal} onRespond={respond} busy={busy} />}

      {!w.weaving && w.status !== "proposed" && (
        <div>
          {woven.length > 0 && (
            <ul data-testid="weave-woven-list" className="space-y-4">
              {woven.map(t => (
                <li key={t.id} data-testid="weave-woven-thread" className="rounded-xl border p-5" style={{ borderColor: "rgba(195,165,107,0.45)", background: "hsl(var(--spark-warmth))" }}>
                  <p className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ color: GOLD_DEEP }}>{t.theme}.</p>
                  <p className="mt-2 text-sm sm:text-base leading-relaxed text-[#17171C]">{t.evidence}</p>
                  <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8A91]">Woven from {(t.woven_from || []).map(x => x.label).join(" · ")}</p>
                </li>
              ))}
            </ul>
          )}
          {w.ready && (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {woven.length === 0 && w.status !== "set_aside" ? (
                <>
                  <p className="text-base text-[#56565D] max-w-xl">You've confirmed patterns across {w.have.length} of your maps. Spark can now look for the single thread that connects them — and where it doesn't.</p>
                  <button data-testid="weave-propose-btn" disabled={busy} onClick={() => act(() => proposeWeave(userId))} className="spark-btn-primary disabled:opacity-60">Weave the thread</button>
                </>
              ) : (
                <button data-testid="weave-again-btn" disabled={busy} onClick={() => act(() => weaveAgain(userId))} className="spark-btn-secondary disabled:opacity-60">
                  {woven.length ? "Look for another thread" : "Look again"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
